/**
 * EditableProductRow - Product row within a set editor
 *
 * Displays a product with its name, isDefault checkbox,
 * and dynamic feature value inputs.
 */
import React from "react";
import type {
  EnrichedPackageStructureProduct,
  LeafProductPickerItem,
} from "../../../../types/adminBilling";
import { FeatureValueInput } from "./FeatureValueInput";

export interface EditableProductRowProps {
  product: EnrichedPackageStructureProduct;
  leafProduct?: LeafProductPickerItem;
  onRemove: () => void;
  onToggleDefault: () => void;
  onFeatureValueChange: (
    featureCode: string,
    value: string | number | boolean
  ) => void;
}

export const EditableProductRow: React.FC<EditableProductRowProps> = ({
  product,
  leafProduct,
  onRemove,
  onToggleDefault,
  onFeatureValueChange,
}) => {
  const displayName =
    leafProduct?.name ??
    product.productName ??
    `(nieznany produkt) ${product.productId}`;
  const features = leafProduct?.features ?? [];

  return (
    <div className="bg-white rounded border border-neutral-200 p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-neutral-900">
            {displayName}
          </span>
          <label className="flex items-center gap-1.5 text-xs text-neutral-600">
            <input
              type="checkbox"
              checked={product.isDefault}
              onChange={onToggleDefault}
              className="h-3.5 w-3.5 text-primary-600 border-neutral-300 rounded"
            />
            Domyślny
          </label>
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Usuń produkt: ${displayName}`}
          className="text-xs text-red-600 hover:text-red-800 px-2 py-1"
        >
          Usuń
        </button>
      </div>

      {/* Feature values form (C7) */}
      {features.length > 0 && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {features.map((f) => (
            <FeatureValueInput
              key={f.id}
              featureCode={f.featureCode}
              constraintType={f.constraintType}
              constraintConfig={f.constraintConfig}
              value={product.featureValues?.[f.featureCode]}
              onChange={(val) => onFeatureValueChange(f.featureCode, val)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
